import React, { useEffect, useRef, useState } from "react";
import { Radio, ShoppingCart, Calendar, ChevronDown, ChevronUp } from "lucide-react";

/**
 * POSLiveFeed — Floating Live-Panel rechts unten in der Kinokasse.
 * WebSocket zu /api/pos/live: zeigt Echtzeit-Feed anderer Kassen
 * (Verkäufe, kassierte Reservierungen), damit Mitarbeiter im
 * Nachbar-Raum sofort sehen, was gerade passiert.
 */
export default function POSLiveFeed() {
  const [status, setStatus] = useState("connecting"); // connecting | live | fallback
  const [peers, setPeers] = useState(0);
  const [events, setEvents] = useState([]); // {id, source, order_number, total, movie_title, guest_name, by_name, at, seats_count, items_count}
  const [collapsed, setCollapsed] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = base.replace(/^https/i, "wss").replace(/^http/i, "ws")
      + `/api/pos/live?token=${encodeURIComponent(localStorage.getItem("mh_token") || "")}`;
    let ws, hb, reconnect, closed = false;

    const connect = () => {
      setStatus("connecting");
      try { ws = new WebSocket(url); } catch { setStatus("fallback"); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus("live");
        hb = setInterval(() => { try { ws.send("ping"); } catch { /* ignore */ } }, 25000);
      };
      ws.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data);
          if (ev.type === "hello") setPeers(ev.peers || 1);
          else if (ev.type === "order") {
            setEvents((cur) => [{ ...ev, _uid: `${ev.order_id}-${Date.now()}` }, ...cur].slice(0, 8));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setStatus("fallback");
        if (hb) clearInterval(hb);
        if (!closed) reconnect = setTimeout(connect, 4000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => {
      closed = true;
      if (hb) clearInterval(hb);
      if (reconnect) clearTimeout(reconnect);
      if (ws) { try { ws.close(); } catch { /* ignore */ } }
    };
  }, []);

  const SRC_ICON = { pos_sell: ShoppingCart, reservation_checkout: Calendar };
  const SRC_LABEL = { pos_sell: "POS-Verkauf", reservation_checkout: "Reservierung kassiert" };

  const pillCls = status === "live" ? "border-[#D4AF37]/60 text-[#D4AF37] bg-[#D4AF37]/10"
    : status === "connecting" ? "border-[#8B7328]/60 text-[#EEDC82] bg-[#8B7328]/10"
    : "border-[#2A2A2A] text-[#A8A398] bg-[#0F0F0F]";

  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)]" data-testid="pos-live-feed">
      <div className="rounded-lg overflow-hidden bg-[#0A0A0A]/95 backdrop-blur-sm border border-[#1A1A1A] shadow-[0_18px_60px_rgba(0,0,0,0.65)]">
        <button onClick={() => setCollapsed(!collapsed)} data-testid="pos-live-toggle"
          className="w-full flex items-center gap-2 px-3 py-2.5 border-b border-[#1A1A1A] hover:bg-[#0F0F0F] transitions-safe">
          <div className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest ${pillCls}`}>
            <Radio size={9} className={status === "live" ? "animate-pulse" : ""} />
            {status === "live" ? `LIVE · ${peers}` : status === "connecting" ? "Verbinde …" : "Getrennt"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#A8A398] flex-1 text-left ml-2">
            Kassen-Live-Feed{events.length ? ` · ${events.length}` : ""}
          </div>
          {collapsed ? <ChevronUp size={14} className="text-[#575148]" /> : <ChevronDown size={14} className="text-[#575148]" />}
        </button>
        {!collapsed && (
          <div className="max-h-80 overflow-y-auto" data-testid="pos-live-events">
            {!events.length && (
              <div className="p-6 text-center text-[10px] uppercase tracking-widest text-[#575148]">
                Warte auf Aktivität anderer Kassen …
              </div>
            )}
            {events.map((ev) => {
              const Icon = SRC_ICON[ev.source] || ShoppingCart;
              return (
                <div key={ev._uid} data-testid={`pos-live-event-${ev.order_number}`}
                  className="flex items-start gap-3 px-3 py-2.5 border-b border-[#0F0F0F] hover:bg-[#0F0F0F]/60 animate-in fade-in slide-in-from-right-2 duration-500">
                  <Icon size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <div className="text-sm font-medium truncate">{ev.movie_title || ev.guest_name || SRC_LABEL[ev.source] || "Verkauf"}</div>
                      <div className="font-mono text-[#D4AF37] text-sm shrink-0">{(ev.total || 0).toFixed(2)} €</div>
                    </div>
                    <div className="text-[10px] text-[#A8A398] mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className="uppercase tracking-widest text-[#575148]">{SRC_LABEL[ev.source] || ev.source}</span>
                      {ev.seats_count > 0 && <span>· {ev.seats_count} Sitz{ev.seats_count === 1 ? "" : "e"}</span>}
                      {ev.items_count > 0 && <span>· {ev.items_count} Artikel</span>}
                      {ev.by_name && <span>· {ev.by_name}</span>}
                    </div>
                    <div className="text-[9px] text-[#575148] font-mono mt-0.5">
                      #{ev.order_number} · {new Date(ev.at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
